import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { History, Search, Check, X, Clock, ChevronRight } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';
import { cn, getPatientName, formatDate } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';

export function HistoryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'accept' | 'reject' | 'missed'>('all');

  // Filters run server-side so the page count matches the filtered set.
  // Debounce the search so we don't refetch per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => {
    setPage(1);
  }, [filter, debouncedSearch]);

  const { data, isLoading } = useQuery({
    queryKey: ['approvals', 'history', user?.hospital_id, page, filter, debouncedSearch],
    queryFn: () =>
      api.approvals.history({
        hospitalId: user?.hospital_id ?? undefined,
        page,
        pageSize: 20,
        decision: filter === 'all' ? undefined : filter,
        search: debouncedSearch || undefined,
      }),
    placeholderData: (prev) => prev,
  });

  const filtered = data?.items ?? [];

  return (
    <div className="px-5 py-5 lg:px-8 lg:py-8 max-w-lg lg:max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-stone-100 flex items-center justify-center">
          <History className="w-5 h-5 text-stone-600" />
        </div>
        <div>
          <h2 className="font-sans text-lg font-extrabold text-foreground leading-none">
            History
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Past decisions
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or reference..."
          className="pl-9"
        />
      </div>

      {/* Filter chips */}
      <div className="flex gap-2 mb-5">
        {(['all', 'accept', 'reject', 'missed'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              'px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-150 cursor-pointer',
              filter === f
                ? 'bg-foreground text-background'
                : 'bg-secondary text-muted-foreground hover:bg-stone-200',
            )}
          >
            {f === 'all' ? 'All' : f === 'accept' ? 'Accepted' : f === 'reject' ? 'Rejected' : 'Missed'}
          </button>
        ))}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[72px] rounded-2xl" />
          ))}
        </div>
      ) : filtered.length > 0 ? (
        <div className="space-y-2">
          {filtered.map((item, i) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="flex items-center gap-3 p-3.5 rounded-2xl bg-card border border-border hover:shadow-sm transition-shadow cursor-pointer"
              onClick={() => navigate(`/history/${item.id}?from=/history`)}
            >
              <div
                className={cn(
                  'w-9 h-9 rounded-xl flex items-center justify-center shrink-0',
                  item.status === 'expired'
                    ? 'bg-stone-100 text-stone-500'
                    : item.decision === 'accept'
                      ? 'bg-emerald-50 text-emerald-600'
                      : 'bg-red-50 text-red-600',
                )}
              >
                {item.status === 'expired' ? (
                  <Clock className="w-4 h-4" strokeWidth={2.5} />
                ) : item.decision === 'accept' ? (
                  <Check className="w-4 h-4" strokeWidth={2.5} />
                ) : (
                  <X className="w-4 h-4" strokeWidth={2.5} />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">
                  {getPatientName(item.patient_info)}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[11px] font-mono text-muted-foreground">
                    {item.ihalaty_reference}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {item.decided_at
                      ? formatDate(item.decided_at)
                      : item.timer_expires_at
                        ? formatDate(item.timer_expires_at)
                        : ''}
                  </span>
                </div>
              </div>

              <Badge
                variant={
                  item.status === 'expired'
                    ? 'secondary'
                    : item.decision === 'accept'
                      ? 'success'
                      : 'destructive'
                }
              >
                {item.status === 'expired'
                  ? 'Missed'
                  : item.decision === 'accept'
                    ? 'Accepted'
                    : 'Rejected'}
              </Badge>

              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="py-16 text-center">
          <p className="text-muted-foreground text-sm">
            {search || filter !== 'all'
              ? 'No matching results'
              : 'No history yet'}
          </p>
        </div>
      )}

      {/* Pagination */}
      {data && data.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground px-3">
            {page} / {data.pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= data.pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
